// Centralized authentication management for both Spotify and Applauss tokens
import axios from 'axios';
import {
  createTokenStorage,
  createSingleFlight,
  isValidJWT,
  getJWTExpiration,
} from '@applauss/api-client';
import { getBackendBaseUrl } from './backendUrl';
import { forgetFeedContext } from './feedContext';

/**
 * Storage keys used in localStorage
 */
const STORAGE_KEYS = {
  // Spotify OAuth tokens (from port 3001)
  SPOTIFY_ACCESS_TOKEN: 'spotify_access_token',
  SPOTIFY_REFRESH_TOKEN: 'spotify_refresh_token',
  SPOTIFY_TOKEN_EXPIRY: 'spotify_token_expiry',
  SPOTIFY_USER_PROFILE: 'spotify_user_profile',

  // Applauss backend tokens (from port 8000)
  APPLAUSS_ACCESS_TOKEN: 'applauss_access_token',
  APPLAUSS_REFRESH_TOKEN: 'applauss_refresh_token',
  APPLAUSS_TOKEN_EXPIRY: 'applauss_token_expiry',
  APPLAUSS_USER: 'applauss_user',
  APPLAUSS_SEARCH_HISTORY: 'applauss_search_history',

  // Anonymous-tracking identifiers (see apps/api/docs/ANONYMOUS_USERS.md)
  APPLAUSS_DEVICE_ID: 'applauss_device_id',
  APPLAUSS_ANON_CONSENT: 'applauss_anon_consent',
};

// Pre-consent opt-out flag. Read once by the migration below, then deleted.
const LEGACY_ANON_OPTOUT_KEY = 'applauss_anon_tracking_optout';

// Set when a visitor declines the consent gate. It lives here rather than in
// onboardingProgress so this module stays a leaf — everything imports
// authManager, and importing a page-domain module back would be a cycle waiting
// to happen. `onboardingProgress.markOnboardingDeclined` writes it alongside the
// preferences a decliner needs; these two primitives are all the consent
// lifecycle needs.
const ONBOARDING_DECLINED_KEY = 'onboarding_declined';

/** @returns {boolean} True if the visitor declined the onboarding consent gate. */
export function isOnboardingDeclined() {
  return localStorage.getItem(ONBOARDING_DECLINED_KEY) === 'true';
}

export function markOnboardingDeclinedFlag() {
  localStorage.setItem(ONBOARDING_DECLINED_KEY, 'true');
}

function clearOnboardingDeclined() {
  localStorage.removeItem(ONBOARDING_DECLINED_KEY);
}

const SESSION_STORAGE_KEYS = {
  APPLAUSS_SESSION_ID: 'applauss_session_id',
};

// Shared token store for the Applauss JWT lifecycle. Key names above are
// load-bearing: changing them logs out every existing session.
const applaussTokenStorage = createTokenStorage({
  accessToken: STORAGE_KEYS.APPLAUSS_ACCESS_TOKEN,
  refreshToken: STORAGE_KEYS.APPLAUSS_REFRESH_TOKEN,
  user: STORAGE_KEYS.APPLAUSS_USER,
  expiry: STORAGE_KEYS.APPLAUSS_TOKEN_EXPIRY,
});

// ============================================================================
// Anonymous tracking helpers
// ============================================================================

/**
 * Generate a UUID v4 using the browser crypto API, with a non-crypto fallback
 * for environments where crypto.randomUUID is unavailable (older Safari).
 * @returns {string}
 */
function generateUUIDv4() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * The two recordable answers to the onboarding consent gate. A third state —
 * *undecided* — is the absence of the record, and it means **no tracking**:
 * nothing is minted until the visitor says yes.
 *
 * Browser Do Not Track is deliberately NOT consulted — the explicit choice at
 * the gate is the only signal that governs. Rationale in
 * `apps/api/docs/ANONYMOUS_USERS.md` → "Privacy / consent".
 */
export const ANONYMOUS_CONSENT = {
  GRANTED: 'granted',
  DECLINED: 'declined',
};

/**
 * One-shot conversion of browsers that predate the consent gate, in this
 * order: the legacy opt-out flag wins, otherwise an existing device_id is
 * grandfathered to 'granted' so existing anonymous users keep their taste
 * profile and are never re-prompted. A browser with neither stays undecided.
 *
 * Converts at most once: writing a consent record (here or via
 * setAnonymousConsent) is what retires it. A browser with neither signal has
 * nothing to convert, so this stays a cheap no-op probe for them.
 * @returns {string|null} The migrated value, or null if nothing to migrate.
 */
function migrateLegacyAnonymousConsent() {
  const legacyOptOut = localStorage.getItem(LEGACY_ANON_OPTOUT_KEY);

  let migrated = null;
  if (legacyOptOut === 'true') {
    migrated = ANONYMOUS_CONSENT.DECLINED;
  } else if (localStorage.getItem(STORAGE_KEYS.APPLAUSS_DEVICE_ID)) {
    migrated = ANONYMOUS_CONSENT.GRANTED;
  }

  if (migrated) {
    localStorage.setItem(STORAGE_KEYS.APPLAUSS_ANON_CONSENT, migrated);
    if (migrated === ANONYMOUS_CONSENT.DECLINED) {
      // "Declined" means no identifier, not merely no headers. A browser that
      // opted out under the old flag could still be holding a device_id, and
      // peekDeviceId (read at login/register/claim-anonymous) doesn't consult
      // consent — leaving it would merge their history server-side.
      rotateDeviceId();
    }
  }
  if (legacyOptOut !== null) {
    localStorage.removeItem(LEGACY_ANON_OPTOUT_KEY);
  }
  return migrated;
}

/**
 * @returns {'granted'|'declined'|null} The recorded choice, or null when the
 *   visitor is still undecided (which means: do not track).
 */
export function getAnonymousConsent() {
  const stored = localStorage.getItem(STORAGE_KEYS.APPLAUSS_ANON_CONSENT);
  if (stored === ANONYMOUS_CONSENT.GRANTED || stored === ANONYMOUS_CONSENT.DECLINED) {
    return stored;
  }
  return migrateLegacyAnonymousConsent();
}

/**
 * The single predicate every anonymous-tracking surface gates on.
 * @returns {boolean} True only when consent was explicitly granted.
 */
export function hasAnonymousConsent() {
  return getAnonymousConsent() === ANONYMOUS_CONSENT.GRANTED;
}

/**
 * Record the visitor's choice (onboarding gate, or the /cookies page). Both
 * side effects live here rather than at the call sites, so every path that
 * records a choice gets them:
 *
 * - granting clears the onboarding-decline marker, without which a decliner who
 *   comes back through the banner would stay classified as declined forever;
 * - declining drops the identifier itself, not just the permission to send it.
 *   `peekDeviceId` is read straight from storage at login/register/
 *   claim-anonymous without consulting consent, so a leftover id would still
 *   get their history merged server-side.
 *
 * @param {'granted'|'declined'} consent
 */
export function setAnonymousConsent(consent) {
  if (consent !== ANONYMOUS_CONSENT.GRANTED && consent !== ANONYMOUS_CONSENT.DECLINED) {
    return;
  }
  localStorage.setItem(STORAGE_KEYS.APPLAUSS_ANON_CONSENT, consent);
  localStorage.removeItem(LEGACY_ANON_OPTOUT_KEY);
  if (consent === ANONYMOUS_CONSENT.GRANTED) {
    clearOnboardingDeclined();
  } else {
    rotateDeviceId();
  }
}

/**
 * Whether a request from this browser can identify its caller at all — a JWT,
 * or a consented anonymous device_id. The API's `_resolve_identity` 422s
 * without one, so consent-first surfaces check this before calling an
 * identity-required endpoint.
 * @returns {boolean}
 */
export function hasApiIdentity() {
  return getApplaussAccessToken() !== null || peekDeviceId() !== null;
}

// Read-or-generate, with no consent check of their own. The exported wrappers
// below each check once; getAnonymousHeaders — which runs in the axios
// onRequest interceptor for every request — checks once and then calls these,
// rather than paying the check three times per request.
function mintDeviceId() {
  let id = localStorage.getItem(STORAGE_KEYS.APPLAUSS_DEVICE_ID);
  if (!id) {
    id = generateUUIDv4();
    localStorage.setItem(STORAGE_KEYS.APPLAUSS_DEVICE_ID, id);
  }
  return id;
}

function mintSessionId() {
  let id = sessionStorage.getItem(SESSION_STORAGE_KEYS.APPLAUSS_SESSION_ID);
  if (!id) {
    id = generateUUIDv4();
    sessionStorage.setItem(SESSION_STORAGE_KEYS.APPLAUSS_SESSION_ID, id);
  }
  return id;
}

/**
 * Lazily generate (on first call) and return the persistent anonymous
 * device_id. Returns null until consent has been granted — an undecided
 * visitor must reach the gate with nothing minted yet.
 * @returns {string|null}
 */
export function getOrCreateDeviceId() {
  return hasAnonymousConsent() ? mintDeviceId() : null;
}

/**
 * Lazily generate (on first call per tab) and return the session_id.
 * @returns {string|null}
 */
export function getOrCreateSessionId() {
  return hasAnonymousConsent() ? mintSessionId() : null;
}

/**
 * Build the anonymous-tracking headers to attach to every request.
 * Returns an empty object until consent is granted, so the backend
 * dependency short-circuits cleanly.
 * @returns {Object}
 */
export function getAnonymousHeaders() {
  if (!hasAnonymousConsent()) {
    return {};
  }
  return {
    'X-Device-Id': mintDeviceId(),
    'X-Session-Id': mintSessionId(),
  };
}

/**
 * Drop the device_id (localStorage) and session_id (sessionStorage). Called
 * from both ``clearApplaussAuth`` (covers refresh-failure / 401 paths that
 * never reach ``logout``) and from ``saveApplaussAuth`` after a successful
 * authentication. Rotating on login closes the shared-browser bleed window
 * where User A never explicitly logs out and User B inherits the device_id.
 *
 * The consent record is deliberately left alone: rotating an identifier is not
 * a withdrawal of consent, and clearing it here would silently re-prompt the
 * visitor at the gate after every logout or refresh failure.
 */
export function rotateDeviceId() {
  localStorage.removeItem(STORAGE_KEYS.APPLAUSS_DEVICE_ID);
  sessionStorage.removeItem(SESSION_STORAGE_KEYS.APPLAUSS_SESSION_ID);
}

/**
 * Snapshot the device_id BEFORE rotation. Used by callers that need to send
 * the value to ``/auth/claim-anonymous`` and only then rotate.
 * @returns {string|null}
 */
export function peekDeviceId() {
  return localStorage.getItem(STORAGE_KEYS.APPLAUSS_DEVICE_ID);
}

/**
 * Save Spotify tokens to localStorage
 * @param {string} accessToken - Spotify access token
 * @param {string} refreshToken - Spotify refresh token
 * @param {number} expiresIn - Token expiry time in seconds
 */
export function saveSpotifyTokens(accessToken, refreshToken, expiresIn) {
  localStorage.setItem(STORAGE_KEYS.SPOTIFY_ACCESS_TOKEN, accessToken);
  localStorage.setItem(STORAGE_KEYS.SPOTIFY_REFRESH_TOKEN, refreshToken);

  if (expiresIn) {
    const expiryTime = Date.now() + (parseInt(expiresIn) * 1000);
    localStorage.setItem(STORAGE_KEYS.SPOTIFY_TOKEN_EXPIRY, expiryTime.toString());
  }

  console.log('✅ Spotify tokens saved to localStorage');
}

/**
 * Get Spotify tokens from localStorage
 * @returns {Object|null} Spotify tokens or null if not found
 */
export function getSpotifyTokens() {
  const accessToken = localStorage.getItem(STORAGE_KEYS.SPOTIFY_ACCESS_TOKEN);
  const refreshToken = localStorage.getItem(STORAGE_KEYS.SPOTIFY_REFRESH_TOKEN);
  const expiry = localStorage.getItem(STORAGE_KEYS.SPOTIFY_TOKEN_EXPIRY);

  if (!accessToken || !refreshToken) {
    return null;
  }

  return {
    access_token: accessToken,
    refresh_token: refreshToken,
    expiry: expiry ? parseInt(expiry) : null,
    isExpired: expiry ? Date.now() > parseInt(expiry) : false
  };
}

/**
 * Save Applauss authentication response to localStorage
 * @param {Object} authResponse - Response from Applauss /auth/login/spotify endpoint
 * @param {string} authResponse.access_token - Applauss JWT access token
 * @param {string} authResponse.refresh_token - Applauss refresh token
 * @param {number} authResponse.expires_in - Token expiry time in seconds
 * @param {Object} authResponse.user - User information
 * @param {Array} authResponse.recent_recommendations - Recent search history
 * @param {string} authResponse.provider - Auth provider (email, google, facebook, spotify)
 */
export function saveApplaussAuth(authResponse, provider = null) {
  const { access_token, refresh_token, expires_in, user, recent_recommendations } = authResponse;

  // Validate access token JWT format (required)
  // Note: refresh_token is an opaque token (not JWT), so we don't validate it
  if (!isValidJWT(access_token)) {
    console.error('❌ Invalid access token format - not a valid JWT');
    throw new Error('Invalid access token format');
  }

  // Calculate expiry time
  // Priority: 1) Extract from JWT exp claim (most reliable)
  //           2) Use expires_in from backend (fallback)
  let expiryTime = getJWTExpiration(access_token);

  if (!expiryTime && expires_in) {
    // Fallback: calculate from expires_in (subject to client clock drift)
    expiryTime = Date.now() + (parseInt(expires_in) * 1000);
    console.warn('⚠️ Using client clock to calculate token expiry (no exp claim in JWT)');
  }

  applaussTokenStorage.setTokens({
    accessToken: access_token,
    refreshToken: refresh_token,
    expiry: expiryTime,
  });

  // A save always replaces the refresh-token slot: if this auth response
  // carries no refresh token, drop any previously stored one rather than
  // letting it survive into the new session (shared-browser bleed).
  if (!refresh_token) {
    localStorage.removeItem(STORAGE_KEYS.APPLAUSS_REFRESH_TOKEN);
  }

  // Save user info with provider
  if (user) {
    applaussTokenStorage.setUser({
      ...user,
      provider: provider || user.provider || authResponse.provider || 'email'
    });
  }

  // Save recent recommendations (search history)
  if (recent_recommendations) {
    localStorage.setItem(STORAGE_KEYS.APPLAUSS_SEARCH_HISTORY, JSON.stringify(recent_recommendations));
  }

  console.log('✅ Applauss authentication saved:', {
    user_id: user?.id,
    email: user?.email,
    provider: provider || user?.provider || 'email',
    expires_in: expires_in,
    search_history_count: recent_recommendations?.length || 0
  });

  // Rotate the anonymous device_id after a successful login. Subsequent
  // requests carry no anonymous headers (the dependency would short-circuit
  // on the JWT anyway, but this prevents User A's leftover device_id from
  // ever bleeding into User B's session if they share a browser).
  rotateDeviceId();

  // Dispatch event to notify components that auth changed
  window.dispatchEvent(new CustomEvent('authChanged'));
}

/**
 * Get Applauss authentication from localStorage
 * @returns {Object|null} Applauss auth data or null if not found
 */
export function getApplaussAuth() {
  const accessToken = applaussTokenStorage.getAccessToken();

  if (!accessToken) {
    return null;
  }

  return {
    access_token: accessToken,
    refresh_token: applaussTokenStorage.getRefreshToken(),
    expiry: applaussTokenStorage.getExpiry(),
    isExpired: applaussTokenStorage.isExpired(),
    user: applaussTokenStorage.getUser()
  };
}

/**
 * Get Applauss access token (for use in API headers)
 * @returns {string|null} Access token or null if not found/expired
 */
export function getApplaussAccessToken() {
  // Cheap path — no user JSON.parse: this runs on every API request via the
  // interceptor, so it only touches the token and expiry keys.
  const accessToken = applaussTokenStorage.getAccessToken();

  if (!accessToken) {
    // Don't log warning during initial OAuth flow - this is expected
    return null;
  }

  if (applaussTokenStorage.isExpired()) {
    console.warn('⚠️ Applauss token expired');
    return null;
  }

  return accessToken;
}

/**
 * Whether the stored session is refreshable-and-stale: an access token
 * exists, it is expired, and a refresh token is available. Interceptor hot
 * path — reads only the token/expiry keys, never parses the user object.
 * @returns {boolean}
 */
export function needsApplaussTokenRefresh() {
  return Boolean(
    applaussTokenStorage.getAccessToken() &&
    applaussTokenStorage.isExpired() &&
    applaussTokenStorage.getRefreshToken()
  );
}

/**
 * Check if user is authenticated with Applauss
 * @returns {boolean} True if authenticated and token is valid
 */
export function isApplaussAuthenticated() {
  const auth = getApplaussAuth();
  return auth !== null && !auth.isExpired;
}

/**
 * Get Applauss user information
 * @returns {Object|null} User object or null if not found
 */
export function getApplaussUser() {
  const auth = getApplaussAuth();
  return auth?.user || null;
}

/**
 * Merge a patch into the stored user and notify the app.
 *
 * The UI renders from `applauss_user`, never from the API, so a mutation that
 * doesn't land here stays invisible until the next /auth/validate.
 *
 * @param {Object} patch - Fields to merge into the stored user
 */
export function updateApplaussUser(patch) {
  const user = applaussTokenStorage.getUser();
  if (!user) return;

  applaussTokenStorage.setUser({ ...user, ...patch });
  window.dispatchEvent(new CustomEvent('authChanged'));
}

/**
 * Clear all Spotify tokens from localStorage
 */
export function clearSpotifyTokens() {
  localStorage.removeItem(STORAGE_KEYS.SPOTIFY_ACCESS_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.SPOTIFY_REFRESH_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.SPOTIFY_TOKEN_EXPIRY);
  localStorage.removeItem(STORAGE_KEYS.SPOTIFY_USER_PROFILE);
  console.log('🗑️ Spotify tokens cleared');
}

/**
 * Clear all Applauss authentication from localStorage.
 *
 * Also rotates the anonymous device_id so that any session that ended via a
 * 401/refresh-failure (not via the explicit ``logout()`` button) starts a
 * fresh anonymous identity. Without this hook a refresh-failed user would
 * keep their old device_id and any further activity would attach to it.
 */
export function clearApplaussAuth() {
  applaussTokenStorage.clear();
  localStorage.removeItem(STORAGE_KEYS.APPLAUSS_SEARCH_HISTORY);
  rotateDeviceId();
  console.log('🗑️ Applauss authentication cleared');
}

/**
 * Clear all authentication data (logout)
 */
export function logout() {
  clearSpotifyTokens();
  clearApplaussAuth();

  // Also clear onboarding preferences so user goes through onboarding again.
  // The decline marker goes with them: leaving it behind would route the
  // logged-out user straight to the listing instead of onboarding.
  // The live city goes too: persistOnboardingPreferences keeps a stored one over the
  // onboarding profile's, so one left here would follow the next account on this device.
  // So does this tab's last feed and search: HomeRedirect prefers the remembered feed over
  // the account's city, and the search back-link would show the previous query.
  localStorage.removeItem('user_preferences');
  localStorage.removeItem('onboarding_completed');
  localStorage.removeItem('lastSelectedCity');
  localStorage.removeItem('lastSelectedCountry');
  forgetFeedContext();
  try {
    sessionStorage.removeItem('lastSearchQuery');
  } catch {
    // Storage disabled: nothing was stored.
  }
  clearOnboardingDeclined();

  console.log('👋 User logged out - all tokens and preferences cleared');

  // Dispatch event to notify components that user logged out
  window.dispatchEvent(new CustomEvent('authChanged'));
}

/**
 * Get search history from localStorage
 * @returns {Array} Recent recommendations array
 */
export function getSearchHistory() {
  const historyStr = localStorage.getItem(STORAGE_KEYS.APPLAUSS_SEARCH_HISTORY);
  if (!historyStr) {
    return [];
  }

  try {
    return JSON.parse(historyStr);
  } catch (e) {
    console.error('Failed to parse search history:', e);
    return [];
  }
}

/**
 * Get full authentication state
 * @returns {Object} Complete auth state
 */
export function getAuthState() {
  return {
    spotify: getSpotifyTokens(),
    applauss: getApplaussAuth(),
    isFullyAuthenticated: getSpotifyTokens() !== null && isApplaussAuthenticated()
  };
}

// ============================================================================
// CENTRALIZED TOKEN REFRESH LOGIC
// ============================================================================

/**
 * Centralized function to refresh Applauss access token using refresh token.
 * This is the ONLY place where token refresh should happen to avoid race
 * conditions: the single-flight wrapper collapses concurrent calls into one
 * `/auth/refresh` request, and every caller settles with its result.
 *
 * @returns {Promise<Object>} Refresh result with new token data
 * @throws {Error} If refresh fails or no refresh token available
 */
export const refreshApplaussToken = createSingleFlight(async () => {
  const auth = getApplaussAuth();

  if (!auth || !auth.refresh_token) {
    throw new Error('No refresh token available');
  }

  try {
    console.log('🔄 Attempting to refresh expired token...');

    const API_BASE_URL = getBackendBaseUrl();

    const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
      refresh_token: auth.refresh_token
    });

    if (response.data.status === 'success' && response.data.access_token) {
      // Merge existing user data with new data from backend.
      // /auth/refresh owns every field it returns (display_name included), so a null from
      // it must overwrite; `picture` is the one it never sends, and the spread preserves it.
      const userData = {
        ...auth.user,              // Existing user data (picture, etc)
        ...response.data.user      // New data from backend (id, email, display_name, providers)
      };

      console.log('🔍 Refresh response user data:', response.data.user);
      console.log('🔍 Merged user data:', userData);

      // Save the new tokens
      saveApplaussAuth({
        access_token: response.data.access_token,
        refresh_token: response.data.refresh_token || auth.refresh_token,
        expires_in: response.data.expires_in || 3600,
        user: userData
      });

      console.log('✅ Token refreshed successfully');

      return {
        success: true,
        access_token: response.data.access_token,
        user: userData
      };
    }

    throw new Error('Invalid refresh response');
  } catch (error) {
    console.error('❌ Failed to refresh token:', error.response?.data || error.message);

    // If refresh fails (e.g., refresh token is invalid/expired), clear auth
    if (error.response?.status === 401 || error.response?.status === 403) {
      clearApplaussAuth();
    }

    throw error;
  }
});
